
import { Back } from "gsap";
import { IReelConfig } from "../../../dev/reel/IData/IReelData";
import { IWinRatios } from "./IWinConfig";
import { CustomEase } from "gsap/dist/CustomEase";
import { ReelSetBuilder } from "../../../dev/utils/ReelSetBuilder"

interface IGameDim {
    readonly landscape:{
        WIDHT: number;
        HEIGHT: number;
    };
    readonly portrait:{
        WIDHT: number;
        HEIGHT: number;
    };
}

class IBaseConfig {
    BASE_DIM: IGameDim;
    REEL_ENGINE_CONFIG: IReelConfig;
    WIN_RATIOS: IWinRatios;
}

export class GameConfig implements IBaseConfig {

    public static instance: GameConfig = new GameConfig();
    private constructor() { if (!GameConfig.instance) GameConfig.instance = this; };

    // public static instance:GameConfig = new GameConfig();
    BASE_DIM: IGameDim = {
        landscape: {
            WIDHT: 1280, HEIGHT: 720
        },
        portrait: {
            WIDHT: 600, HEIGHT: 720
        }
    }
    
    // **  Reel Config ** //
    REEL_ENGINE_CONFIG: IReelConfig = {

        // Creates Overflow Symbols.
        OVERFLOW: true,

        // How many symbols should be displayed as part of the winArea and how many reels there are.
        WINDOW_AMOUNT: 3,

        // How many extra Symbols will be added for the stop reel. Increasing the distance for the reel to stop which simplifies other easing stops.
        STOP_DISTANCE: 0,

        // Define Reelsets for the reels to use.
        REEL_SETS: ReelSetBuilder.createSpinReels({
            amountOfReels: 5,
            maxVisibleSize: 5,
            minimumSize:50,
            fillerSymbols: {
                amount: 5,
                type: [ "L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
                maxAmountInView:1
                }
            },
            [
                {
                    name: "NORMAL",
                    symbols: [
                        { type: "S", amount: 3, maxAmountInView: 1 },
                        { type: "WC1", amount: [0,0,3,3,3], excludeWithSymbol: ["WC2,WC3"], maxAmountInView: 1 },
                        { type: "WC2", amount: [3,3,3,0,0], excludeWithSymbol: ["WC1,WC3"], maxAmountInView: 1 },
                        { type: "WC3", amount: [0,3,3,3,0], excludeWithSymbol: ["WC1,WC2"], maxAmountInView: 1 },
                    ]
                },
                {
                    name: "RESPIN",
                    symbols: [
                        { type: "S", amount: 3, maxAmountInView: 1 },
                        { type: "WC1", amount: [0,0,3,3,3], excludeWithSymbol: ["WC2,WC3"], maxAmountInView: 1 },
                        { type: "WC2", amount: [3,3,3,0,0], excludeWithSymbol: ["WC1,WC3"], maxAmountInView: 1 },
                        { type: "WC3", amount: [0,3,3,3,0], excludeWithSymbol: ["WC1,WC2"], maxAmountInView: 1 },
                    ]
                },
                {
                    name: "FREESPIN",
                    symbols: [
                        { type: "S", amount: 3, maxAmountInView: 1 },
                        { type: "WC1", amount: [0,0,3,3,3], excludeWithSymbol: ["WC2,WC3"], maxAmountInView: 1 },
                        { type: "WC2", amount: [3,3,3,0,0], excludeWithSymbol: ["WC1,WC3"], maxAmountInView: 1 },
                        { type: "WC3", amount: [0,3,3,3,0], excludeWithSymbol: ["WC1,WC2"], maxAmountInView: 1 },
                    ]
                },
                {
                    name: "FREESPIN_SETUP",
                    fillerReplace:["X","F1", "F2", "F3", "F4", "F5","F6","F7"],
                    symbols: []
                },
            ]
        ),
       
        DIM: {
            PADDING_X: 0,
            PADDING_Y: 0,
            WIDTH: 144,
            HEIGHT: 432,
        },
        // Define the duration for specific states.
        DURATION: {

            START: 0.3,
            FAST_START: 0.2,
            SPIN: 0.25 / 5,
            FAST_SPIN: 0.15 / 5,

            STOP: 0.4,
            MERGE_STOP: 0.95,
            FAST_STOP: 0.22,
            QUICK_STOP: 0.25,

            ATTENTION_STOP: 1.8,

            // Define the minimum time from the start -> stop;
            MINIMUM_SPIN_TIME: 3, // Normal
            MINIMUM_FAST_SPIN_TIME: 1, // Fast

            EXPAND: 0.25
        },
        // Define the delay for specific states.
        DELAY: {
            START: 0.3/2.5,
            FAST_START: 0.2/2.5,
            STOP: 0.25,
            MERGE_STOP: 0.25,
            FAST_STOP: 0.1,
            QUICK_STOP: 0.01,
        },
        // Define the Easeing for specific states.
        EASING: {
            START: Back.easeIn.config(1.5),
            FAST_START: Back.easeIn.config(1.25),
            STOP: Back.easeOut.config(0.64),
            MERGE_STOP: CustomEase.create("merge","M0,0 C0,0 0.054,0.294 0.076,0.4 0.097,0.504 0.146,0.736 0.162,0.8 0.178,0.866 0.213,0.91 0.236,0.94 0.32,1.048 0.835,0.872 0.9,0.9 0.976,0.932 1,1 1,1"),

            FAST_STOP: Back.easeOut.config(0.7),
            QUICK_STOP: Back.easeOut.config(0.7),

            ATTENTION_STOP: Back.easeOut.config(0.2),
            // FAST_STOP: Elastic.easeOut.config(0.8, 0.55),
            // QUICK_STOP:  Elastic.easeOut.config(0.8, 0.55),
            
            EXPAND: Back.easeInOut.config(1.7),
            NUDGE: CustomEase.create("nudge", "M0,0 C0,0 0.264,-0.156 0.3,0 0.321,0.084 0.559,0.91 0.6,1 0.628,1.062 0.737,1.02 0.8,1 0.88,0.974 1,1 1,1"),
        }
    }

    WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                text: "Tiny Win",
                duration: 0,
            },
            {
                ratio: 1.5,
                text: "Small Win",
                duration: 0.2,
            },
            {
                ratio: 3,
                text: "Medium Win",
                duration: 0.3,
            },
            {
                ratio: 5,
                text: "Large Win",
                duration: 0.7,
            }
        ],
        bigWin: [
            {
                ratio: 15,
                text: "Big Win",
                duration: 5.5,
            },
            {
                ratio: 25,
                text: "Super Win",
                duration: 6,
            },
            {
                ratio: 50,
                text: "Mega Win",
                duration: 6,
            },
            {
                ratio: 100,
                text: "Epic Win",
                duration: 6,
            },
            {
                ratio: 175,
                text: "Wicked Win",
                duration: 14,
            }
        ]
    };
}